import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import {useSnackbar} from 'notistack';
import dayjs from 'dayjs';

import {useApplicationContext} from 'ApplicationContext';
import {settingsApi} from 'Pages/Admin/Settings/settingsApi';
import {apiErrorMessage} from 'Pages/Calendar/errorText';

import {TimeEntry, TimeEntryList} from './labelTaskTypes';

interface Props {
    open: boolean;
    /** Vorbelegte Auftragsnummer; `null` = frei eingeben. */
    orderNumber: string | null;
    onClose: () => void;
    /** Nach erfolgreicher Buchung, etwa zum Neuladen einer Liste. */
    onBooked?: (entry: TimeEntry) => void;
}

/** Wie die Dauer angegeben wird: als Stundenzahl oder als Von/Bis-Uhrzeiten. */
type DurationMode = 'hours' | 'times';

/**
 * Zeit auf einen Labelwin-Auftrag buchen.
 *
 * Gebucht wird immer für den angemeldeten Benutzer — eine Personenauswahl gibt es bewusst nicht
 * (der Server nimmt den LabelBenutzer des Kontos). Eine Buchung lässt sich über diese
 * Schnittstelle **nicht** ändern oder löschen; darauf weist der Dialog hin, bevor jemand es
 * hinterher im Labelwin-Client korrigieren muss.
 */
export const TimeEntryDialog: React.FC<Props> = ({open, orderNumber, onClose, onBooked}) => {
    const {t} = useTranslation();
    const context = useApplicationContext();
    const {enqueueSnackbar} = useSnackbar();
    const http = useMemo(() => settingsApi(context.api), [context.api]);

    const canViewOrderTotals = context.hasPermission('labelwin.time.viewall');

    const today = dayjs().format('YYYY-MM-DD');
    const [order, setOrder] = useState('');
    const [date, setDate] = useState(today);
    const [mode, setMode] = useState<DurationMode>('hours');
    const [hours, setHours] = useState('');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [work, setWork] = useState('');
    const [busy, setBusy] = useState(false);
    const [totals, setTotals] = useState<TimeEntryList | undefined>(undefined);

    // Beim Öffnen frisch beginnen — ein alter Entwurf zu einem anderen Auftrag wäre eine Falle.
    useEffect(() => {
        if (!open) return;
        setOrder(orderNumber ?? '');
        setDate(dayjs().format('YYYY-MM-DD'));
        setMode('hours');
        setHours('');
        setFrom('');
        setTo('');
        setWork('');
        setTotals(undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, orderNumber]);

    /*
     * Die Summen des Auftrags zeigen Buchungen aller Mitarbeiter samt Bewertung — der Endpunkt
     * hängt deshalb an einer eigenen Berechtigung, und ohne sie bleibt die Kopfzeile einfach weg.
     */
    const loadTotals = React.useCallback((forOrder: string) => {
        if (!canViewOrderTotals || !forOrder.trim()) return;
        http.get<TimeEntryList>(`/TimeTracking/Order/${encodeURIComponent(forOrder.trim())}`)
            .then((r) => setTotals(r.data))
            .catch(() => setTotals(undefined));
    }, [canViewOrderTotals, http]);

    useEffect(() => {
        if (open && orderNumber) loadTotals(orderNumber);
    }, [open, orderNumber, loadTotals]);

    const valid = order.trim().length > 0
        && work.trim().length > 0
        && (mode === 'hours'
            ? Number(hours) > 0 && Number(hours) <= 24
            : from.length > 0 && to.length > 0 && from < to);

    const book = async (): Promise<void> => {
        setBusy(true);
        try {
            const r = await http.post<TimeEntry>('/TimeTracking', undefined, {
                orderNumber: order.trim(),
                date,
                hours: mode === 'hours' ? Number(hours) : null,
                from: mode === 'times' ? from : null,
                to: mode === 'times' ? to : null,
                work: work.trim(),
            });
            enqueueSnackbar(
                t('timeTracking.booked', {hours: r.data.hours, order: r.data.orderNumber ?? order.trim()}),
                {variant: 'success'});
            onBooked?.(r.data);
            loadTotals(order);
            onClose();
        } catch (e) {
            // 400/403/409/503 tragen die Begründung des Servers in Klartext.
            enqueueSnackbar(apiErrorMessage(e, t('timeTracking.failed')), {variant: 'error'});
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle>{t('timeTracking.title')}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{mt: 1}}>
                    {totals && (
                        <Typography variant="body2" color="text.secondary">
                            {t('timeTracking.totals', {
                                total: totals.totalHours,
                                open: totals.openHours,
                                entries: totals.count,
                            })}
                        </Typography>
                    )}

                    <TextField fullWidth size="small" label={t('timeTracking.field.orderNumber')}
                               value={order}
                               disabled={orderNumber !== null}
                               onChange={(e) => setOrder(e.target.value)}/>

                    {/* max = heute: der Server lehnt Zukunftsdaten ohnehin ab. */}
                    <TextField fullWidth size="small" type="date" label={t('timeTracking.field.date')}
                               value={date}
                               slotProps={{inputLabel: {shrink: true}, htmlInput: {max: today}}}
                               onChange={(e) => setDate(e.target.value)}/>

                    <ToggleButtonGroup exclusive size="small" value={mode}
                                       onChange={(_, next) => next && setMode(next as DurationMode)}>
                        <ToggleButton value="hours">{t('timeTracking.mode.hours')}</ToggleButton>
                        <ToggleButton value="times">{t('timeTracking.mode.times')}</ToggleButton>
                    </ToggleButtonGroup>

                    {mode === 'hours' ? (
                        <TextField fullWidth size="small" type="number" label={t('timeTracking.field.hours')}
                                   value={hours}
                                   slotProps={{htmlInput: {min: 0.25, max: 24, step: 0.25}}}
                                   onChange={(e) => setHours(e.target.value)}/>
                    ) : (
                        <Stack direction="row" spacing={2}>
                            <TextField fullWidth size="small" type="time" label={t('timeTracking.field.from')}
                                       value={from}
                                       slotProps={{inputLabel: {shrink: true}}}
                                       onChange={(e) => setFrom(e.target.value)}/>
                            <TextField fullWidth size="small" type="time" label={t('timeTracking.field.to')}
                                       value={to}
                                       slotProps={{inputLabel: {shrink: true}}}
                                       onChange={(e) => setTo(e.target.value)}/>
                        </Stack>
                    )}

                    <TextField fullWidth size="small" label={t('timeTracking.field.work')}
                               multiline minRows={2}
                               value={work}
                               onChange={(e) => setWork(e.target.value)}
                               helperText={t('timeTracking.workHint')}/>

                    <Alert severity="info">{t('timeTracking.noUndoHint')}</Alert>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy}>{t('timeTracking.cancel')}</Button>
                {/*
                  * Während der Anfrage gesperrt und der Dialog schließt erst nach der Antwort:
                  * die Buchung ist nicht idempotent — ein Doppelklick wären zwei Buchungen.
                  */}
                <Button variant="contained" onClick={() => void book()} disabled={busy || !valid}>
                    {t('timeTracking.book')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TimeEntryDialog;
